/**
 * CYP ASD Observation Module — Task Configuration
 *
 * This file defines the structure and content for each observation task used
 * in Children and Young People (CYP) Autism Spectrum Disorder (ASD) assessments.
 *
 * The observation module allows clinicians to select from a menu of 10
 * standardised CYP tasks. Each task captures structured clinical observations
 * across four sections:
 *   A — Task Administration (materials, setup)
 *   B — Observations (engagement, communication, reciprocity, etc.)
 *   C — Contextual Factors (anything that may have influenced the child's performance)
 *   D — DSM-5 Domain Contribution (mapping observations to diagnostic criteria)
 *
 * Field types used across the config:
 *   'radio'    — single-select options rendered as selectable chips
 *   'checkbox' — multi-select options rendered as toggleable chips
 *   'dropdown' — standard <select> element
 *   'richtext' — auto-resizing textarea for clinical narrative (DictationTextArea)
 *
 * Fields with `hasOther: true` automatically append an "Other (describe)" option
 * that reveals a free-text input when selected, giving clinicians flexibility to
 * capture observations that fall outside the pre-defined categories.
 */

import beachSceneThumbnail from '../../../../assets/images/toc/beach_scene.png';
import cartoonThumbnail from '../../../../assets/images/toc/cartoon.png';
import tuesdayBookThumbnail from '../../../../assets/images/toc/tuesday_book.png';
import freefallBookThumbnail from '../../../../assets/images/toc/freefall_book.png';

// ---------------------------------------------------------------------------
// Helper factories — keep individual field definitions compact and consistent
// ---------------------------------------------------------------------------

/** Single-select field rendered as selectable chip buttons */
const radio = (id, label, options, hasOther = true) => ({
  id, label, type: 'radio', options, hasOther,
});

/** Multi-select field rendered as toggleable chip buttons */
const checkbox = (id, label, options, hasOther = true) => ({
  id, label, type: 'checkbox', options, hasOther,
});

/** Select dropdown (with optional free-text "Other" entry) */
const dropdown = (id, label, options, hasOther = true) => ({
  id, label, type: 'dropdown', options, hasOther,
});

/** Rich-text area for clinical narrative */
const richtext = (id, label) => ({
  id, label, type: 'richtext',
});

/**
 * Ordered list of clinician-administered questions that can be
 * individually toggled as "asked". Mirrors the SSI probing-question
 * UX so clinicians get consistent interaction across modules.
 *
 * Pass `{ nested: true }` for a branch that sits under a parent topic
 * (e.g. "If living at home with parents" under "Living arrangements").
 * Nested groups render as quieter sub-subheadings so the parent topic
 * does not need repeating in the label.
 */
const questionnaire = (id, label, questions, { nested = false } = {}) => ({
  id, label, type: 'questionnaire', questions, nested,
});

// ---------------------------------------------------------------------------
// DSM-5 Domain Definitions (identical across all tasks)
// ---------------------------------------------------------------------------

/**
 * The seven DSM-5 ASD diagnostic criteria domains.
 * Section D of every task lets the clinician record whether each domain was
 * evidenced during that specific task. Final clinical interpretation still
 * requires synthesis across all tasks in the full assessment.
 */
export const DSM5_DOMAINS = [
  {
    id: 'A1',
    shortTitle: 'Social-emotional reciprocity',
    title: 'Deficits in social-emotional reciprocity',
    tooltip: 'Abnormal social approach, failure of normal back-and-forth conversation, reduced sharing of interests or emotions',
  },
  {
    id: 'A2',
    shortTitle: 'Nonverbal communication',
    title: 'Deficits in nonverbal communicative behaviours used for social interaction',
    tooltip: 'Poorly integrated verbal and nonverbal communication, abnormalities in eye contact, body language, or deficits in understanding/use of gestures',
  },
  {
    id: 'A3',
    shortTitle: 'Relationships',
    title: 'Deficits in developing, maintaining, and understanding relationships',
    tooltip: 'Difficulties adjusting behaviour to suit social contexts, difficulties in sharing imaginative play, absence of interest in peers',
  },
  {
    id: 'B1',
    shortTitle: 'Stereotyped behaviour/speech',
    title: 'Stereotyped or repetitive motor movements, use of objects, or speech',
    tooltip: 'Echolalia / scripted speech, motor mannerisms, repetitive language, use of objects in a repetitive manner',
  },
  {
    id: 'B2',
    shortTitle: 'Insistence on sameness',
    title: 'Insistence on sameness, inflexible adherence to routines, or ritualised patterns',
    tooltip: 'Extreme distress at small changes, difficulties with transitions, rigid thinking patterns',
  },
  {
    id: 'B3',
    shortTitle: 'Restricted interests',
    title: 'Highly restricted, fixated interests that are abnormal in intensity or focus',
    tooltip: 'Strong attachment to or preoccupation with unusual objects, excessively circumscribed interests',
  },
  {
    id: 'B4',
    shortTitle: 'Sensory reactivity',
    title: 'Hyper- or hyporeactivity to sensory input or unusual sensory interests',
    tooltip: 'Apparent indifference to pain/temperature, adverse response to specific sounds or textures, excessive smelling or touching of objects, visual fascination with lights or movement',
  },
];

/**
 * Evidence rating options — aligned with the SSI module's options so that
 * observation evidence and SSI evidence use the same vocabulary, making
 * cross-module synthesis straightforward on the Review page.
 */
export const DSM5_EVIDENCE_OPTIONS = [
  'Clear evidence',
  'Partial evidence',
  'No evidence',
  'Inconclusive',
];

// ---------------------------------------------------------------------------
// Contextual Factors — shared options for Section C across all tasks
// ---------------------------------------------------------------------------

/**
 * Contextual factors that may have influenced the child's presentation during
 * a task. These are multi-select checkboxes; some carry conditional sub-fields
 * (e.g. selecting "Possible masking/compensation" reveals a reason dropdown).
 */
export const CONTEXTUAL_FACTOR_OPTIONS = [
  { id: 'none', label: 'None observed' },
  { id: 'anxiety', label: 'Anxiety' },
  { id: 'lowMotivation', label: 'Low motivation' },
  { id: 'cognitiveLoad', label: 'Cognitive load evident' },
  { id: 'languageDifficulties', label: 'Language difficulties' },
  {
    id: 'masking',
    label: 'Possible masking/compensation',
    conditional: {
      type: 'dropdown',
      label: 'Reason',
      options: [
        'Overly formal description',
        'Rule-based narration',
        'Monitors examiner feedback',
        'Self-correction for social appropriateness',
      ],
      hasOther: true,
    },
  },
  { id: 'fatigue', label: 'Fatigue' },
  { id: 'behaviouralDysregulation', label: 'Behavioural dysregulation' },
  { id: 'environmentalDistraction', label: 'Environmental distraction' },
  { id: 'sensoryDistractions', label: 'Sensory distractions' },
  { id: 'other', label: 'Other', hasTextInput: true },
];

// ---------------------------------------------------------------------------
// Shared observation field groups
// ---------------------------------------------------------------------------

/**
 * Base engagement fields appear in every task. Individual tasks extend this
 * array with task-specific items (e.g. "page turn coordination" in Task 2).
 */
const ENGAGEMENT_BASE = [
  radio('entryIntoTask', 'Entry into task', ['Immediate', 'Hesitant', 'Resistant', 'Refused']),
  radio('interestLevel', 'Interest level', [
    'Sustained engagement throughout', 'Variable engagement',
    'Frequently disengages', 'Requires re-engagement', 'Refuses',
  ]),
  radio('needForPrompting', 'Need for prompting', ['None', 'Minimal', 'Frequent']),
];

/**
 * Core communication observation fields shared across all tasks.
 * Captures verbal, nonverbal, and gestural communication quality.
 */
const COMMUNICATION_BASE = [
  radio('spontaneity', 'Spontaneity', ['Spontaneous', 'Prompted', 'Minimal']),
  radio('elaboration', 'Elaboration', ['Rich detail', 'Basic', 'Sparse', 'Repetitive']),
  radio('speech', 'Speech', ['Fluent', 'Limited', 'Scripted / formulaic']),
  radio('prosody', 'Prosody/intonation', ['Typical', 'Atypical', 'Monotone', 'Exaggerated', 'Sing-song']),
  radio('gestureSpeech', 'Gesture-speech coordination', [
    'Fully integrated throughout', 'Partial / inconsistent',
    'Gesture without speech', 'Speech without gesture', 'Absent',
  ]),
  radio('eyeContact', 'Eye contact modulation', ['Context-appropriate', 'Reduced', 'Atypical', 'Avoidant']),
  radio('facialExpression', 'Facial expression range', ['Broad', 'Limited', 'Flat']),
  radio('conventionalGestures', 'Use of conventional gestures', ['Appropriate', 'Simplified', 'Unconventional', 'Absent']),
];

/**
 * Restrictive, repetitive, and sensory behaviour fields.
 * Note: sensory behaviours is a multi-select (checkbox) because several
 * modalities can co-occur in a single observation.
 */
const RRBS_FIELDS = [
  radio('motorMannerisms', 'Motor mannerisms', ['None', 'Possible', 'Clear']),
  radio('stereotypedLanguage', 'Stereotyped or idiosyncratic language', ['None', 'Possible', 'Clear']),
  checkbox('sensoryBehaviours', 'Sensory behaviours', [
    'Visual', 'Auditory', 'Tactile', 'Movement/proprioceptive', 'Mixed', 'Not observed',
  ]),
];

/** Salient observations — free-text fields for clinical notes and strengths */
const SALIENT_OBS_FIELDS = [
  richtext('clinicalNotes', 'Clinical Notes'),
  richtext('strengthsObserved', 'Strengths observed'),
];

// ---------------------------------------------------------------------------
// Task 1 — Description of a Picture
// ---------------------------------------------------------------------------

const TASK_1 = {
  id: 1,
  shortTitle: 'Description of a Picture',
  fullTitle: 'Description of a Picture',
  focusOfObservation:
    'Obtain a sample of the participant\'s spontaneous language and communication, as well as a sense of what captures their interest.',
  available: true,
  relevantCriteria: ['A1', 'A2', 'B3', 'B4'],
  externalResources: [
    {
      label: 'Beach Scene',
      url: 'https://drive.google.com/file/d/1QUvmnhYvRF3GBC5jFIj3Q3ha_eb_XHoF/view?usp=drive_link',
      thumbnail: beachSceneThumbnail,
    },
    {
      label: 'Cartoon',
      url: 'https://drive.google.com/file/d/14kFaT-Q8NGqfbYyePrK3px_h-_yNGgSv/view?usp=drive_link',
      thumbnail: cartoonThumbnail,
    },
  ],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('descriptionOfPictureSteps', 'Steps', [
          'Say: "Let\'s look at this picture now. Can you tell me about it? What is happening here?"',
        ]),
        dropdown('materialsUsed', 'Materials Used', ['Map Scene', 'Resort Scene', 'Feast Scene']),
        {
          ...radio('secondPictureUsed', 'Second Picture Used', ['No', 'Yes'], false),
          conditional: {
            triggerValue: 'Yes',
            field: dropdown('secondPictureReason', 'Reason', [
              'Insufficient language sample', 'Lack of interest',
            ]),
          },
        },
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('initiation', 'Initiation', ['Spontaneous', 'Prompt-dependent', 'Minimal']),
            radio('backAndForth', 'Back-and-forth exchange', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        {
          title: '4. Descriptive Style',
          fields: [
            radio('focusOfAttention', 'Focus of Attention', [
              'Broad scene integration', 'Mix of global and details',
              'Detail-focused', 'Atypical / idiosyncratic focus',
            ]),
            radio('topicMaintenance', 'Topic maintenance', [
              'Maintains topic appropriately', 'Drifts off topic',
              'Perseverative focus', 'Requires redirection',
            ]),
            radio('primaryAttentionalTarget', 'Primary attentional target', [
              'Social elements (people/interactions)', 'Actions/events', 'Objects',
              'Sensory features', 'Background details', 'Text/labels',
              'Specific details (describe)', 'Hard to determine',
            ], false),
            radio('narrativeOrganisation', 'Narrative organisation', [
              'Coherent narrative sequence', 'Describes isolated events without sequence',
              'Fragmented / disorganised', 'Minimal narrative', 'Not applicable',
            ]),
            radio('qualityOfDescription', 'Quality of description', [
              'Concrete/literal', 'Functional', 'Interpretive', 'Inferential',
            ]),
            radio('contextualInformation', 'Use of contextual information', [
              'Integrates context meaningfully', 'Partially integrates context',
              'Describes elements in isolation',
            ]),
            radio('creativeLanguage', 'Creative / metaphorical language use', ['Present', 'Some', 'Absent']),
          ],
        },
        {
          title: '5. Social Insight',
          fields: [
            radio('refPeopleActions', 'References to people\'s actions', [
              'Frequent', 'Occasional', 'Minimal', 'Not applicable',
            ]),
            radio('responseToHumour', 'Response to humour', [
              'Spontaneous sharing', 'Acknowledges only', 'None observed',
              'No recognition or response noted', 'Not applicable',
            ]),
            radio('refEmotions', 'References to emotions', [
              'Spontaneous', 'Prompted', 'Absent', 'Not applicable',
            ]),
            radio('perspectiveTaking', 'Perspective-taking', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
            radio('understandingRelationships', 'Understanding of relationships', [
              'Age-appropriate', 'Concrete', 'Limited', 'Not applicable',
            ]),
            radio('relatesPersonalExperience', 'Relates picture to personal experience', [
              'Spontaneous', 'Prompted', 'Absent', 'Not applicable',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('flexAttention', 'Flexibility of Attention', ['Flexible', 'Somewhat restricted', 'Highly restricted']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('responseExaminerNarration', 'Response to examiner perspective/comments during narration', ['Flexible', 'Partial', 'Limited']),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 2 — Telling a Story from a Book
// ---------------------------------------------------------------------------

const TASK_2 = {
  id: 2,
  shortTitle: 'Telling a Story from a Book',
  fullTitle: 'Telling a Story from a Book',
  focusOfObservation:
    'Obtain a sample of the participant\'s spontaneous language and communication, as well as a sense of what captures their interest. In addition, evaluate the participant\'s response to conventional humour, their spontaneous comments about how the characters in the story are feeling, and the degree to which they can convey continuity in a story.',
  available: true,
  relevantCriteria: ['A1', 'B2', 'B3', 'B4'],
  externalResources: [
    {
      label: 'Tuesday Book',
      url: 'https://drive.google.com/file/d/1ILdnetNnF9pbxJlPjVPfqJy9UwJ_sg0K/view?usp=drive_link',
      thumbnail: tuesdayBookThumbnail,
    },
    // Freefall is an alternate picture-book stimulus for the same story-telling
    // task — clinicians pick whichever book fits the appointment. Same Drive
    // pattern as Tuesday; opens in a separate window during live OBS sessions.
    {
      label: 'Freefall',
      url: 'https://drive.google.com/file/d/1QB6_zyhmO8HyOWFrh-oTj0W0BkQ2jdEq/view?usp=drive_link',
      thumbnail: freefallBookThumbnail,
    },
  ],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('storyFromBookSteps', 'Steps', [
          'Say: "Let\'s look at this book. It tells a story in pictures. See, it starts out with..." — Describe the first picture in the book.',
          'Say: "Can you tell me the story as we go along? You go first. Then, I\'ll take a turn."',
        ]),
        dropdown('materialsUsed', 'Materials Used', ['Book 1', 'Book 2']),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        {
          title: '1. Engagement and Approach',
          fields: [
            ...ENGAGEMENT_BASE,
            radio('pageTurnCoordination', 'Page turn coordination', [
              'Checks in with examiner / Waits / Shares control',
              'Turns pages without checking / Rushes ahead',
              'Waits for examiner to initiate',
              'Resistant to moving on', 'Needs prompting',
            ]),
          ],
        },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('initiation', 'Initiation', ['Spontaneous', 'Prompt-dependent', 'Minimal']),
            radio('backAndForth', 'Back-and-forth exchange', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('responseClinicianTurn', 'Response to clinician taking a narrative turn', [
              'Allows turn and listens', 'Allows turn but interrupts',
              'Resists or attempts to control narrative',
              'Insists on completing story alone',
              'Not applicable (turn not taken)',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal', 'Not applicable']),
          ],
        },
        {
          title: '4. Narrative Organisation & Focus',
          fields: [
            radio('narrativeCoherence', 'Narrative Coherence', [
              'Coherent, sequenced story with clear beginning-middle-end',
              'Describes pictures individually with minimal linking',
              'Focuses on isolated details rather than story arc',
              'Fragmented / difficult to follow', 'Minimal narrative',
            ]),
            radio('storyContinuity', 'Story continuity', [
              'Maintains storyline', 'Loses track', 'Focuses on isolated frames',
            ]),
            radio('primaryAttentionalTarget', 'Primary Attentional Target', [
              'Story plot / sequence', 'Characters\' actions',
              'Characters\' emotions/relationships', 'Isolated visual details',
              'Background elements', 'Hard to determine',
            ]),
            radio('pacing', 'Pacing', [
              'Appropriate', 'Rushes through', 'Slow / hesitant',
              'Over focuses on details', 'Stalls / needs prompts',
            ]),
            radio('temporalLanguage', 'Temporal language / sequencing markers', [
              'Frequent', 'Occasional', 'Minimal', 'Absent',
            ]),
            radio('creativeLanguage', 'Creative / metaphorical language use', ['Present', 'Some', 'Absent']),
          ],
        },
        {
          title: '5. Social Insight',
          fields: [
            radio('characterMotivations', 'Character motivations / intentions', [
              'Identifies', 'Partially identifies', 'Does not identify',
            ]),
            radio('causalUnderstanding', 'Causal understanding', [
              'Explains why events happen',
              'Describes events without causal links',
              'No causal understanding evident',
            ]),
            radio('emotionLabelling', 'Emotion labelling (characters)', [
              'Frequent and spontaneous', 'Occasional', 'Prompted', 'Absent',
            ]),
            radio('perspectiveTaking', 'Perspective-taking', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
            radio('refRelationships', 'References to relationships between characters', [
              'Spontaneous', 'Prompted', 'Absent',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('flexAttention', 'Flexibility of Attention', ['Flexible', 'Somewhat restricted', 'Highly restricted']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('responseExaminerComments', 'Response to examiner perspective/comments', ['Flexible', 'Partial', 'Limited']),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 3 — Demonstration
// ---------------------------------------------------------------------------

const TASK_3 = {
  id: 3,
  shortTitle: 'Demonstration',
  fullTitle: 'Demonstration',
  focusOfObservation:
    'Assess the participant\'s ability to communicate about a familiar series of actions using gesture or mime with accompanying language, and to report on a routine event.',
  available: true,
  relevantCriteria: ['A2', 'B1', 'B2', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        // Module 4 / online script — verbal scene-setting only (no table drawing
        // from the Module 3 in-person protocol).
        questionnaire('demonstrationSteps', 'Steps', [
          'Say: "Now I want you to pretend something with me. Let\'s pretend that I don\'t know how to brush my teeth, and I need you to teach me!"',
          'Say: "Let\'s pretend that this is the sink, this is the hot water and this is the cold water. This is the toothbrush, this is the toothpaste, and this is the cup."',
          'Say: "Now I want you to show me and tell me how to brush my teeth. Start right at the beginning. You\'ve just come into the bathroom to brush your teeth. What do you do now?"',
          'If participant demonstrates rapidly without speech, or pantomimes without speaking → Say: "That\'s great. Now, tell me and show me again right from the beginning — from when you first came into the bathroom."',
          'If participant talks without producing any gestures → Say: "That\'s great. Now can you try it again, but this time show me and tell me at the same time."',
          'If toothbrushing yields insufficient data, repeat with handwashing using the same approach.',
        ]),
        dropdown('taskAdministered', 'Task Administered', [
          'Toothbrushing', 'Handwashing', 'Toothbrushing and handwashing',
        ]),
        radio('clinicianModelling', 'Clinician Modelling Required', [
          'None', 'Minimal (set scene only)', 'Needed to demonstrate actions',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        {
          title: '1. Engagement and Approach',
          fields: [
            ...ENGAGEMENT_BASE,
            radio('responsePretend', 'Response to pretend scenario', [
              'Engages readily', 'Partial engagement', 'Needs clarification',
              'Confused by pretend context', 'Rejects pretend context',
            ]),
          ],
        },
        {
          title: '2. Communication Within Task',
          fields: [
            ...COMMUNICATION_BASE,
            radio('clarityVerbal', 'Clarity of verbal explanation', [
              'Clear instructional language', 'Basic / functional', 'Vague / incomplete',
            ]),
          ],
        },
        {
          title: '3. Action Organisation & Sequencing',
          fields: [
            radio('sequenceCompleteness', 'Sequence completeness', [
              'Full routine demonstrated', 'Partial routine', 'Isolated action only',
            ]),
            radio('stepOrganisation', 'Step organisation', [
              'Logical / correct order', 'Minor omissions', 'Some steps out of order',
              'Disorganised / fragmented / unclear sequence', 'Minimal sequence',
            ]),
            radio('pacing', 'Pacing', [
              'Appropriate', 'Rushes through', 'Slow / hesitant',
              'Over focuses on details', 'Stalls / needs prompts',
            ]),
            radio('imaginaryObjects', 'Use of imaginary objects / props', [
              'Clear and consistent', 'Partial / inconsistent', 'Absent',
            ]),
            radio('clarityMime', 'Clarity of mime', [
              'Clear and easily understood', 'Vague / unclear', 'Absent',
            ]),
            radio('sceneIncorporation', 'Setting / scene incorporation', [
              'Incorporates sink, faucets, cup as set up', 'Partial incorporation', 'No incorporation',
            ]),
          ],
        },
        { title: '4. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '5. Flexibility & Adaptation',
          fields: [
            radio('flexApproach', 'Flexibility of approach', ['Flexible', 'Somewhat rigid', 'Highly rigid / perseverative']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('responseExaminerComments', 'Response to examiner perspective/comments', ['Flexible', 'Partial', 'Limited']),
            radio('creativeElaboration', 'Creative elaboration', [
              'Adds novel / creative elements', 'Follows basic routine only', 'Repetitive / rigid',
            ]),
          ],
        },
        { title: '6. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 4 — Conversation and Reporting
// ---------------------------------------------------------------------------

const TASK_4 = {
  id: 4,
  shortTitle: 'Conversation and Reporting',
  fullTitle: 'Conversation and Reporting',
  focusOfObservation:
    'To assess the participant\'s ability to engage in reciprocal conversation, report events outside the immediate context, describe nonroutine experiences, and share affect, relationships, and perspectives in a less structured interaction.',
  available: true,
  relevantCriteria: ['A1', 'B1', 'B3', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        dropdown('topicSource', 'Initial Conversation Topic Source', [
          'Session-related discussion',
          'Participant\'s stated interests',
          'Daily life (school/work/home)',
          'Leisure activities',
          'Relationships',
        ]),
        dropdown('nonroutineOpportunity', 'Opportunity for Nonroutine Event Report', [
          'Provided and explored',
          'Attempted but limited response',
          'Not introduced',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Conversational Quality',
          fields: [
            radio('reciprocity', 'Reciprocity', [
              'Balanced give-and-take', 'Mostly one-sided (examiner leads)',
              'Mostly one-sided (participant monologues)', 'Minimal exchange',
            ]),
            radio('topicIntroduction', 'Topic introduction', [
              'Introduces new topics spontaneously', 'Follows examiner topics only',
              'Attempts but tangential', 'Does not introduce topics',
            ]),
            radio('topicMaintenance', 'Topic maintenance', [
              'Maintains topic appropriately', 'Drifts off topic',
              'Perseverative focus', 'Requires redirection',
            ]),
            radio('responseToShifts', 'Response to topic shifts', [
              'Flexible / follows naturally', 'Slow to adapt', 'Resists or ignores',
            ]),
            radio('backAndForth', 'Back-and-forth flow', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('questionAsking', 'Question-asking', [
              'Reciprocal / appropriate', 'Asks but factual only',
              'Asks repetitively', 'Does not ask questions',
            ]),
          ],
        },
        {
          title: '4. Event Reporting',
          fields: [
            radio('eventClarity', 'Clarity of nonroutine event report', [
              'Clear and sequenced', 'Partially clear', 'Vague / disorganised',
              'Unable to provide', 'Not applicable',
            ]),
            radio('temporalMarkers', 'Temporal markers in narrative', [
              'Frequent', 'Occasional', 'Minimal', 'Absent',
            ]),
            radio('affectInReport', 'Affect / emotional colouring in report', [
              'Rich emotional content', 'Some emotional references',
              'Factual / flat', 'Not applicable',
            ]),
          ],
        },
        {
          title: '5. Social Insight',
          fields: [
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
            radio('perspectiveTaking', 'Perspective-taking', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
            radio('understandingRelationships', 'Understanding of relationships', [
              'Age-appropriate', 'Concrete', 'Limited', 'Not applicable',
            ]),
            radio('insightOwnBehaviour', 'Insight into own behaviour / emotions', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('flexAttention', 'Flexibility of Attention', ['Flexible', 'Somewhat restricted', 'Highly restricted']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 5 — Social Difficulties and Annoyance
// ---------------------------------------------------------------------------

const TASK_5 = {
  id: 5,
  shortTitle: 'Social Difficulties and Annoyance',
  fullTitle: 'Social Difficulties and Annoyance',
  focusOfObservation:
    'Evaluate how the participant understands the concepts of friendship, marriage, and other social relationships, and the nature of these relationships. Also, evaluate the participant\'s understanding of why a person might want to be involved in a long-term relationship and of their own possible role in such a relationship.',
  available: true,
  relevantCriteria: ['A1', 'A3', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('questionsAsked', 'Questions to ask in order', [
          'Have you ever had problems getting along with people at school? How about at home? Do you ever get in trouble? Why? What for?',
          'Are there things that other people do that irritate or annoy you? What are they?',
          'What about things you do that annoy others?',
          'Have you ever been teased or bullied? Why do you think?',
          'Have you ever tried to change these things? Have you ever done anything so that others wouldn\'t tease you? How has that worked?',
          'Are there other people you know who get teased or bullied?',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('reciprocity', 'Reciprocity', [
              'Balanced give-and-take', 'Mostly one-sided (examiner leads)',
              'Mostly one-sided (participant monologues)', 'Minimal exchange',
            ]),
            radio('backAndForth', 'Back-and-forth flow', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        {
          title: '4. Social Understanding',
          fields: [
            radio('understandingSocialDifficulties', 'Understanding of own social difficulties', [
              'Clear and insightful', 'Partial / concrete',
              'Limited / vague', 'Does not acknowledge difficulties',
            ]),
            radio('attributionOfCause', 'Attribution of cause for social difficulties', [
              'Identifies own role and others\' roles', 'Blames others only',
              'Blames self only', 'No attribution', 'Not applicable',
            ]),
            radio('bullyingInsight', 'Insight into bullying / teasing experiences', [
              'Reflects on reasons and impact', 'Describes events factually',
              'Minimises or dismisses', 'Denies any experience', 'Not applicable',
            ]),
            radio('desireForChange', 'Desire or attempts to change social situation', [
              'Describes specific strategies', 'General desire without strategy',
              'No apparent desire for change', 'Not applicable',
            ]),
            radio('empathyForOthers', 'Empathy for others who are teased / bullied', [
              'Spontaneous empathic response', 'Acknowledges with prompting',
              'Factual / detached response', 'No response', 'Not applicable',
            ]),
          ],
        },
        {
          title: '5. Emotional Expression',
          fields: [
            radio('affectRange', 'Range of affect when discussing difficulties', [
              'Appropriate and varied', 'Somewhat flat', 'Flat / incongruent',
            ]),
            radio('emotionalVocabulary', 'Emotional vocabulary', [
              'Rich / nuanced', 'Basic', 'Limited', 'Absent',
            ]),
            radio('facialExpressionCongruence', 'Facial expression congruence', [
              'Consistent with content', 'Partial / inconsistent', 'Incongruent',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('perspectiveShifting', 'Ability to shift perspective (own vs others\')', [
              'Shifts readily', 'Shifts with prompting', 'Difficulty shifting',
            ]),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 6 — Emotions
// ---------------------------------------------------------------------------

const TASK_6 = {
  id: 6,
  shortTitle: 'Emotions',
  fullTitle: 'Emotions',
  focusOfObservation:
    'Identify the events or objects that elicit different emotions in the participant, particularly whether they are social in nature or not. In addition, observe how the participant describes their emotions. Also, observe whether the participant exhibits facial expressions or creative uses of language in the context of describing their emotions and others\' emotions, and whether they display insight into typical social relationships that may cause some of these emotions.',
  available: true,
  relevantCriteria: ['A1', 'A3', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('emotionQuestions', 'Questions to ask in __any__ order', [
          'What do you like doing that makes you happy and cheerful?',
          'What kind of things make you feel this way? How do you feel when you are happy? Can you describe it?',
          'What about things you\'re afraid of?',
          'What makes you feel frightened or anxious? How does it feel? What do you do?',
          'What about feeling angry?',
          'What kind of things make you feel that way? How do you feel "inside" when you\'re angry?',
          'Most people have times when they feel sad. What is it like when you\'re sad? Can you describe that?',
          'How do you feel when you\'re sad? What is it like when you\'re sad? Can you describe that?',
          'What about feeling relaxed or content? What kind of things make you feel that way?',
        ]),
        checkbox('emotionsExplored', 'Emotions Explored', [
          'Happy/cheerful',
          'Fear/anxiety',
          'Anger',
          'Sadness',
          'Relaxed/content',
        ], true),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Emotion Understanding',
          fields: [
            radio('emotionIdentification', 'Emotion identification', [
              'Identifies and labels accurately', 'Identifies some emotions',
              'Confuses similar emotions', 'Minimal identification',
            ]),
            radio('emotionElicitors', 'Nature of emotion elicitors', [
              'Primarily social (people / relationships)',
              'Mix of social and non-social',
              'Primarily non-social (objects / activities)',
              'Unclear / vague',
            ]),
            radio('ownVsOthers', 'Own emotions vs others\' emotions', [
              'Describes both with ease', 'Describes own but not others\'',
              'Describes others\' but not own', 'Difficulty with both',
            ]),
            radio('emotionElaboration', 'Elaboration on emotional experiences', [
              'Rich / detailed', 'Basic', 'Sparse / scripted', 'Absent',
            ]),
            radio('emotionRegulationInsight', 'Insight into emotion regulation', [
              'Describes strategies spontaneously', 'Describes with prompting',
              'Limited awareness', 'Not applicable',
            ]),
          ],
        },
        {
          title: '4. Expressive Quality',
          fields: [
            radio('facialExpressionMatch', 'Facial expression matches described emotion', [
              'Consistent and congruent', 'Partial / inconsistent',
              'Flat / absent', 'Incongruent',
            ]),
            radio('creativeLanguageUse', 'Creative language use', [
              'Uses metaphor / figurative language', 'Some creative expression',
              'Literal / factual only', 'Absent',
            ]),
            radio('voiceProsody', 'Vocal prosody when describing emotions', [
              'Varies appropriately', 'Some variation', 'Monotone / flat',
            ]),
          ],
        },
        {
          title: '5. Social Insight',
          fields: [
            radio('socialCausation', 'Links emotions to social causes', [
              'Spontaneous', 'With prompting', 'Limited', 'Not applicable',
            ]),
            radio('perspectiveTaking', 'Perspective-taking about others\' emotions', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
            radio('understandingRelationships', 'Understanding of relationships that cause emotions', [
              'Age-appropriate', 'Concrete', 'Limited', 'Not applicable',
            ]),
            radio('sharedAffect', 'Shared affect during discussion', [
              'Present', 'Reduced', 'Minimal',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('responseNewEmotions', 'Response to introduction of new emotions', [
              'Engages readily', 'Hesitant but explores', 'Resistant / avoidant',
            ]),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 7 — Cartoons
// ---------------------------------------------------------------------------

const TASK_7 = {
  id: 7,
  shortTitle: 'Cartoons',
  fullTitle: 'Cartoons',
  focusOfObservation:
    'Observe the participant\'s use of gesture and its coordination with speech, as well as their response to humour. Also, use this task to obtain an additional language sample from the participant and a sense of their degree of flexibility in adapting a narrative to the audience of the listener. In addition, note any comments the participant makes about emotions and relationships.',
  available: true,
  relevantCriteria: ['A1', 'A3', 'B4'],
  externalResources: [
    {
      label: 'Cartoon',
      url: 'https://drive.google.com/file/d/14kFaT-Q8NGqfbYyePrK3px_h-_yNGgSv/view?usp=drive_link',
      thumbnail: cartoonThumbnail,
    },
  ],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('cartoonsSteps', 'Steps', [
          'Say: "You will now be shown some cartoons which tell a story about a fisherman and a cat. We will go through them on the screen and you then will be asked to retell the story without looking at the pictures."',
          'Encourage them to comment on what is happening on each card.',
          'Stop sharing your screen, then ask them to tell you the story again.',
        ]),
        dropdown('seriesUsed', 'Series Used', [
          'Series A', 'Series B', 'Both',
        ]),
        dropdown('audienceForRetell', 'Audience for Retell', [
          'Clinician only', 'Clinician + observer',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('initiation', 'Initiation', ['Spontaneous', 'Prompt-dependent', 'Minimal']),
            radio('backAndForth', 'Back-and-forth exchange', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
            radio('sharedHumour', 'Shared humour / laughter', [
              'Spontaneous sharing with examiner', 'Laughs alone',
              'Acknowledges humour only', 'No response to humour',
            ]),
          ],
        },
        {
          title: '4. Narrative Organisation & Audience Adaptation',
          fields: [
            radio('narrativeCoherence', 'Narrative Coherence', [
              'Coherent, sequenced narrative',
              'Describes individual panels with minimal linking',
              'Focuses on isolated details rather than storyline',
              'Fragmented / difficult to follow', 'Minimal narrative',
            ]),
            radio('audienceAdaptation', 'Adaptation of narrative to audience', [
              'Adapts language / detail level appropriately',
              'Some adaptation', 'No apparent adaptation',
            ]),
            radio('gestureInNarrative', 'Gesture use in narrative', [
              'Integrated with speech', 'Partial / inconsistent',
              'Absent', 'Present but uncoordinated with speech',
            ]),
            radio('pacing', 'Pacing', [
              'Appropriate', 'Rushes through', 'Slow / hesitant',
              'Over focuses on details', 'Stalls / needs prompts',
            ]),
            radio('creativeLanguage', 'Creative / metaphorical language use', ['Present', 'Some', 'Absent']),
          ],
        },
        {
          title: '5. Social Insight',
          fields: [
            radio('responseToHumour', 'Response to humour in cartoons', [
              'Understands and explains humour', 'Partial understanding',
              'Laughs but cannot explain', 'No recognition of humour',
            ]),
            radio('characterMotivations', 'Character motivations / intentions', [
              'Identifies', 'Partially identifies', 'Does not identify',
            ]),
            radio('refEmotions', 'References to emotions', [
              'Spontaneous', 'Prompted', 'Absent', 'Not applicable',
            ]),
            radio('refRelationships', 'References to relationships between characters', [
              'Spontaneous', 'Prompted', 'Absent',
            ]),
            radio('perspectiveTaking', 'Perspective-taking', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('flexAttention', 'Flexibility of Attention', ['Flexible', 'Somewhat restricted', 'Highly restricted']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('retellAdaptation', 'Adapts retell when audience asks questions', [
              'Flexible / elaborates', 'Repeats same content', 'Becomes confused or frustrated',
            ]),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 8 — Friends, Relationships and Marriage
// ---------------------------------------------------------------------------

const TASK_8 = {
  id: 8,
  shortTitle: 'Friends, Relationships and Marriage',
  fullTitle: 'Friends, Relationships and Marriage',
  focusOfObservation:
    'Evaluate how the participant understands the concepts of friendship, marriage, and other social relationships, and the nature of these relationships. Also, evaluate the participant\'s understanding of why a person might want to be involved in a long-term relationship and of their own possible role in such a relationship.',
  available: true,
  relevantCriteria: ['A1', 'A3', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('questionsAsked', 'Questions to ask in order', [
          'Do you have some friends? Can you tell me about them?',
          'What do you like doing together? How did you get to know them? How often do you get together?',
          'What does being a friend mean to you? How do you know someone is your friend?',
          'How is a friend different from someone whom you just go to school with?',
          'Do you have a boyfriend/girlfriend? What is their name? How old are they?',
          'When did you see them last?',
          'What are they like? What do you like doing together?',
          'How do you know they are your boyfriend/girlfriend?',
          'Where do you want to live when you are older? Who with?',
          'Do you ever think about getting married?',
          'Why do you think people get married?',
          'What would be nice about it? What would be difficult?',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('reciprocity', 'Reciprocity', [
              'Balanced give-and-take', 'Mostly one-sided (examiner leads)',
              'Mostly one-sided (participant monologues)', 'Minimal exchange',
            ]),
            radio('backAndForth', 'Back-and-forth flow', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        {
          title: '4. Understanding of Friendship',
          fields: [
            radio('friendshipDefinition', 'Quality of friendship definition', [
              'Rich / abstract (trust, loyalty, emotional support)',
              'Functional / activity-based (we play together)',
              'Vague or circular (a friend is someone who is friendly)',
              'Cannot define',
            ]),
            radio('friendVsAcquaintance', 'Differentiates friend from acquaintance', [
              'Clear qualitative distinction', 'Partial / concrete distinction',
              'No meaningful distinction', 'Not applicable',
            ]),
            radio('friendshipReciprocity', 'Evidence of reciprocal friendships', [
              'Describes mutual activities and shared interests',
              'Describes one-sided interactions',
              'Describes proximity-based relationships only',
              'Reports no friendships', 'Not applicable',
            ]),
            radio('friendshipMaintenance', 'Friendship initiation and maintenance', [
              'Actively initiates and maintains', 'Maintains but does not initiate',
              'Relies on others to initiate', 'Limited engagement', 'Not applicable',
            ]),
          ],
        },
        {
          title: '5. Understanding of Romantic / Long-Term Relationships',
          fields: [
            radio('romanticUnderstanding', 'Understanding of romantic relationships', [
              'Age-appropriate understanding', 'Concrete / factual',
              'Limited or scripted', 'No understanding expressed', 'Not applicable',
            ]),
            radio('marriageConcept', 'Understanding of why people get married', [
              'Identifies emotional / companionship reasons',
              'Identifies practical / rule-based reasons',
              'Vague or stereotyped response', 'Cannot explain', 'Not applicable',
            ]),
            radio('ownRoleInRelationship', 'Insight into own role in a relationship', [
              'Reflects on own preferences and contribution',
              'Basic / concrete response',
              'Limited or no insight', 'Not applicable',
            ]),
            radio('futureAspirations', 'Future living / relationship aspirations', [
              'Realistic and considered', 'Vague but present',
              'Absent or avoidant', 'Not applicable',
            ]),
          ],
        },
        {
          title: '6. Social Insight',
          fields: [
            radio('perspectiveTaking', 'Perspective-taking', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
            radio('insightOwnSocialRole', 'Insight into own social role / needs', [
              'Present', 'Emerging', 'Limited', 'Not applicable',
            ]),
            radio('emotionalVocabulary', 'Emotional vocabulary when discussing relationships', [
              'Rich / nuanced', 'Basic', 'Limited', 'Absent',
            ]),
          ],
        },
        { title: '7. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '8. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('perspectiveShifting', 'Ability to shift perspective (own vs others\')', [
              'Shifts readily', 'Shifts with prompting', 'Difficulty shifting',
            ]),
          ],
        },
        { title: '9. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 9 — Loneliness
// ---------------------------------------------------------------------------

const TASK_9 = {
  id: 9,
  shortTitle: 'Loneliness',
  fullTitle: 'Loneliness',
  focusOfObservation:
    'Evaluate whether the participant understands the concept of loneliness and how they feel it pertains to them or to other people.',
  available: true,
  relevantCriteria: ['A1', 'A3', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('questionsAsked', 'Questions to ask in order', [
          'Do you ever feel lonely?',
          'Do you think other people your age feel lonely?',
          'Are there things you do to make yourself feel better? What do other people do if they are lonely?',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('reciprocity', 'Reciprocity', [
              'Balanced give-and-take', 'Mostly one-sided (examiner leads)',
              'Mostly one-sided (participant monologues)', 'Minimal exchange',
            ]),
            radio('backAndForth', 'Back-and-forth flow', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        {
          title: '4. Understanding of Loneliness',
          fields: [
            radio('conceptUnderstanding', 'Understanding of the concept of loneliness', [
              'Abstract / nuanced (emotional state, not just being alone)',
              'Concrete / situational (alone = lonely)',
              'Vague or confused', 'Cannot define',
            ]),
            radio('personalRelevance', 'Relevance to self', [
              'Acknowledges and reflects on own experience',
              'Acknowledges but gives minimal detail',
              'Denies experiencing loneliness',
              'Does not understand the question',
            ]),
            radio('othersExperience', 'Understanding that others feel lonely', [
              'Recognises with reasons / empathy',
              'Acknowledges without elaboration',
              'Does not recognise or consider', 'Not applicable',
            ]),
            radio('copingStrategies', 'Coping strategies described', [
              'Social strategies (seek company, talk to someone)',
              'Solitary strategies (hobbies, distraction)',
              'Mixed social and solitary',
              'No strategies identified', 'Not applicable',
            ]),
            radio('selfVsOtherCoping', 'Differentiates own coping from others\'', [
              'Clear differentiation', 'Some differentiation',
              'No differentiation', 'Not applicable',
            ]),
          ],
        },
        {
          title: '5. Emotional Expression',
          fields: [
            radio('affectRange', 'Range of affect when discussing loneliness', [
              'Appropriate and varied', 'Somewhat flat', 'Flat / incongruent',
            ]),
            radio('emotionalVocabulary', 'Emotional vocabulary', [
              'Rich / nuanced', 'Basic', 'Limited', 'Absent',
            ]),
            radio('facialExpressionCongruence', 'Facial expression congruence', [
              'Consistent with content', 'Partial / inconsistent', 'Incongruent',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('perspectiveShifting', 'Ability to shift perspective (own vs others\')', [
              'Shifts readily', 'Shifts with prompting', 'Difficulty shifting',
            ]),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 10 — Creating a Story
// ---------------------------------------------------------------------------

const TASK_10 = {
  id: 10,
  shortTitle: 'Creating a Story',
  fullTitle: 'Creating a Story',
  focusOfObservation:
    'Observe and evaluate the participant\'s creative use of objects in telling a novel story or creating a newscast or commercial.',
  available: true,
  relevantCriteria: ['A1', 'B2', 'B3', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('creatingAStorySteps', 'Steps', [
          'Encourage them to find 5 objects — Explain that they need to be small items so they can hold a few at once.',
          'Say: "Now you and I are going to make up stories using our 5 objects. First I will use my five things and use them to make up a story, and then you will have a turn to make up a story using your five different things."',
        ]),
        checkbox('objectsProvided', 'Objects Provided', [
          'Two functional objects',
          'Three ambiguous objects',
        ], true),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Creative Use of Objects',
          fields: [
            radio('objectUse', 'Use of objects in narrative', [
              'Integrates objects creatively into a coherent story',
              'Uses objects but with limited creativity',
              'Functional use only (literal / concrete)',
              'Does not incorporate objects meaningfully',
            ]),
            radio('symbolicPlay', 'Symbolic / imaginative play with objects', [
              'Clear symbolic use (objects represent something else)',
              'Partial symbolic use', 'Concrete / literal use only',
              'Does not engage with objects',
            ]),
            radio('noveltyOfStory', 'Novelty of story / narrative', [
              'Original and inventive', 'Draws on familiar narratives (TV, books)',
              'Scripted / repetitive', 'Minimal narrative produced',
            ]),
            radio('formatChosen', 'Format chosen by participant', [
              'Story', 'Newscast', 'Commercial', 'Mixed / other',
              'Did not choose independently',
            ]),
          ],
        },
        {
          title: '4. Narrative Organisation',
          fields: [
            radio('narrativeCoherence', 'Narrative coherence', [
              'Coherent with clear beginning-middle-end',
              'Partially coherent', 'Fragmented / difficult to follow',
              'Minimal narrative',
            ]),
            radio('pacing', 'Pacing', [
              'Appropriate', 'Rushes through', 'Slow / hesitant',
              'Over focuses on details', 'Stalls / needs prompts',
            ]),
            radio('creativeLanguage', 'Creative / metaphorical language use', ['Present', 'Some', 'Absent']),
            radio('audienceAwareness', 'Audience awareness', [
              'Engages audience throughout', 'Some awareness',
              'No apparent awareness of audience',
            ]),
          ],
        },
        {
          title: '5. Social Insight',
          fields: [
            radio('characterDevelopment', 'Character development', [
              'Creates characters with motivations / feelings',
              'Basic character descriptions', 'No character development',
            ]),
            radio('refEmotions', 'References to emotions in narrative', [
              'Spontaneous', 'Prompted', 'Absent', 'Not applicable',
            ]),
            radio('refRelationships', 'References to relationships between characters', [
              'Spontaneous', 'Prompted', 'Absent', 'Not applicable',
            ]),
            radio('sharedAffect', 'Shared affect / enjoyment', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('flexAttention', 'Flexibility of Attention', ['Flexible', 'Somewhat restricted', 'Highly restricted']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('responseExaminerComments', 'Response to examiner suggestions / input', [
              'Incorporates readily', 'Acknowledges but continues own narrative',
              'Ignores or resists',
            ]),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 11 — Current Work or School
// ---------------------------------------------------------------------------

const TASK_11 = {
  id: 11,
  shortTitle: 'Current Work or School',
  fullTitle: 'Current Work or School',
  focusOfObservation:
    'Obtain general information about whether the participant is employed and/or in school, whether they have had to leave jobs, and if so, for what reasons. Evaluate whether the participant has a realistic understanding of the possibilities for future employment. In addition, determine what the participant\'s school experience is or was like, and explore whether they have a realistic understanding of the training or experience necessary for future employment.',
  available: true,
  relevantCriteria: ['A1', 'A3', 'B2', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        // Topic groups mirror Daily Living: parent headings for Employment /
        // School, with conditional follow-ups as nested sub-subheadings so the
        // "If employed" / "If not employed" branches are not tickable chips.
        questionnaire('employmentQuestions', 'Employment', [
          'Do you have a job?',
        ]),
        questionnaire('ifEmployedQuestions', 'If employed', [
          'What kind of job is it? How did you find it?',
          'Have you had other jobs before?',
          'Are you happy where you are or would you like to move on to something else eventually? What would it be?',
          'What about your co-workers? Do they seem to be happy or are they ready to move on?',
        ], { nested: true }),
        questionnaire('ifNotEmployedQuestions', 'If not employed', [
          'What do you do during the day?',
          'Did you have a job before? Why did you leave your old job? Was it something you had planned?',
        ], { nested: true }),
        questionnaire('ifNotEmployedWantJobQuestions', 'If not employed and would like a job', [
          'What would it be? What will you need to do to find this type of job?',
        ], { nested: true }),
        questionnaire('ifNotEmployedNoJobQuestions', 'If not employed and would not like a job', [
          'What would you like to do?',
        ], { nested: true }),
        questionnaire('schoolQuestions', 'School', [
          'Are you in school? Where?',
        ]),
        questionnaire('ifInSchoolQuestions', 'If in school', [
          'What courses are you taking?',
          'What year (grade) are you in? How is it going?',
        ], { nested: true }),
        questionnaire('ifNoLongerInSchoolQuestions', 'If no longer in school and not employed', [
          'How far did you go in school? How did it go?',
          'What do you plan to do next? What experience/training would you need to do that?',
        ], { nested: true }),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('reciprocity', 'Reciprocity', [
              'Balanced give-and-take', 'Mostly one-sided (examiner leads)',
              'Mostly one-sided (participant monologues)', 'Minimal exchange',
            ]),
            radio('backAndForth', 'Back-and-forth flow', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        {
          title: '4. Insight and Understanding',
          fields: [
            radio('understandingCurrentSituation', 'Understanding of current situation', [
              'Clear and realistic', 'Partially realistic',
              'Limited / vague', 'Unrealistic or absent',
            ]),
            radio('futureEmploymentInsight', 'Realistic understanding of future employment', [
              'Clear plan with practical steps', 'General awareness without specifics',
              'Vague or unrealistic expectations', 'No consideration of future', 'Not applicable',
            ]),
            radio('understandingSocialRoles', 'Understanding of social roles (employer/employee, teacher/student)', [
              'Age-appropriate understanding', 'Concrete / rule-based understanding',
              'Limited or confused', 'Not applicable',
            ]),
            radio('responsibilityAwareness', 'Awareness of own role in determining outcomes', [
              'Recognises own agency and responsibility', 'Partial awareness',
              'Externalises responsibility', 'No apparent awareness',
            ]),
            radio('jobLeavingInsight', 'Insight into reasons for leaving jobs (if applicable)', [
              'Reflective and insightful', 'Factual without reflection',
              'Blames others / external only', 'Minimises or denies issues', 'Not applicable',
            ]),
          ],
        },
        {
          title: '5. Emotional Expression',
          fields: [
            radio('affectRange', 'Range of affect when discussing work/school', [
              'Appropriate and varied', 'Somewhat flat', 'Flat / incongruent',
            ]),
            radio('emotionalVocabulary', 'Emotional vocabulary', [
              'Rich / nuanced', 'Basic', 'Limited', 'Absent',
            ]),
            radio('facialExpressionCongruence', 'Facial expression congruence', [
              'Consistent with content', 'Partial / inconsistent', 'Incongruent',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('perspectiveShifting', 'Ability to shift perspective (own vs others\')', [
              'Shifts readily', 'Shifts with prompting', 'Difficulty shifting',
            ]),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 12 — Daily Living
// ---------------------------------------------------------------------------

const TASK_12 = {
  id: 12,
  shortTitle: 'Daily Living',
  fullTitle: 'Daily Living',
  focusOfObservation:
    'Obtain factual information and background for socioemotional questions and evaluate the participant\'s understanding and views regarding money, residential arrangements, and leisure activities. Evaluate the degree to which the participant is responsible for themselves in activities of daily living, and whether the participant sees themselves as being responsible for their own actions.',
  available: true,
  relevantCriteria: ['A3', 'B2', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        // Three topic groups mirror the Module 4 Daily Living interview flow.
        questionnaire('moneyQuestions', 'Money', [
          'What about money? Do you take care of your own money?',
          'Where does it come from? Who pays your bills?',
          'Have you ever saved your money to buy something or do something special? What was it?',
        ]),
        // The two follow-up branches are nested groups rather than items in
        // one list: every questionnaire item is individually tickable and
        // draggable into notes, so a bare condition would read as a question
        // to ask. Nested labels render as sub-subheadings under the parent
        // "Living arrangements" topic — no need to repeat that prefix.
        questionnaire('livingQuestions', 'Living arrangements', [
          'Where are you living now?',
        ]),
        questionnaire('livingWithParentsQuestions', 'If living at home with parents', [
          'Have you ever lived away from your parents?',
          'What would be different about living on your own?',
          'Would you like it better?',
          'What would be difficult?',
        ], { nested: true }),
        questionnaire('livingIndependentlyQuestions', 'If living on their own', [
          'How did you find the place where you live now?',
          'Who do you live with?',
          'Can you tell me a little about it?',
        ], { nested: true }),
        questionnaire('leisureQuestions', 'Leisure Activities', [
          'What do you like to do in your spare time?',
          'What about going out?',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('reciprocity', 'Reciprocity', [
              'Balanced give-and-take', 'Mostly one-sided (examiner leads)',
              'Mostly one-sided (participant monologues)', 'Minimal exchange',
            ]),
            radio('backAndForth', 'Back-and-forth flow', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        {
          title: '4. Self-Care and Independence',
          fields: [
            radio('levelOfResponsibility', 'Level of responsibility for daily activities', [
              'Fully independent', 'Mostly independent with some support',
              'Requires significant support', 'Fully dependent on others',
            ]),
            radio('understandingIndependence', 'Understanding of what independence entails', [
              'Realistic and detailed', 'Partially realistic',
              'Vague or idealised', 'Does not understand the concept',
            ]),
            radio('practicalSkillsAwareness', 'Awareness of practical skills needed', [
              'Identifies specific skills and challenges', 'General awareness without specifics',
              'Limited awareness', 'No apparent consideration',
            ]),
            radio('financialUnderstanding', 'Understanding of money management', [
              'Demonstrates planning and saving behaviour', 'Basic understanding',
              'Limited or absent understanding', 'Not applicable',
            ]),
            radio('leisureActivities', 'Nature of leisure activities', [
              'Varied and socially inclusive', 'Varied but predominantly solitary',
              'Restricted to few activities', 'Restricted to single interest',
            ]),
          ],
        },
        {
          title: '5. Routines and Sameness',
          fields: [
            radio('adherenceToRoutines', 'Adherence to routines in daily life', [
              'Flexible / adaptive', 'Prefers routine but tolerates change',
              'Strong preference for routine', 'Rigid adherence to routine',
            ]),
            radio('responseToHypotheticalChange', 'Response to hypothetical changes in living situation', [
              'Considers change openly', 'Acknowledges change with some anxiety',
              'Resistant to considering change', 'Distressed by the idea of change',
            ]),
            radio('flexibilityDailyStructure', 'Flexibility in daily structure', [
              'Spontaneous and varied', 'Somewhat structured',
              'Highly structured', 'Inflexible / ritualistic',
            ]),
          ],
        },
        {
          title: '6. Emotional Expression',
          fields: [
            radio('affectRange', 'Range of affect when discussing daily living', [
              'Appropriate and varied', 'Somewhat flat', 'Flat / incongruent',
            ]),
            radio('emotionalVocabulary', 'Emotional vocabulary', [
              'Rich / nuanced', 'Basic', 'Limited', 'Absent',
            ]),
            radio('facialExpressionCongruence', 'Facial expression congruence', [
              'Consistent with content', 'Partial / inconsistent', 'Incongruent',
            ]),
          ],
        },
        { title: '7. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '8. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('perspectiveShifting', 'Ability to shift perspective (own vs others\')', [
              'Shifts readily', 'Shifts with prompting', 'Difficulty shifting',
            ]),
          ],
        },
        { title: '9. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Task 13 — Plans and Hopes
// ---------------------------------------------------------------------------

const TASK_13 = {
  id: 13,
  shortTitle: 'Plans and Hopes',
  fullTitle: 'Plans and Hopes',
  focusOfObservation:
    'Obtain information about what the participant anticipates in the future. Give the participant an opportunity to describe any goals or aspirations that they may have; this is also intended to provide a positive end to the interview.',
  available: true,
  relevantCriteria: ['A3', 'B2', 'B3', 'B4'],
  sections: [
    {
      id: 'A',
      title: 'Task Administration',
      fields: [
        questionnaire('questionsAsked', 'Questions to ask in order', [
          'Do you have plans or dreams for the future of things that you would like to do, or have, or see happen to you? Tell me about them.',
        ]),
      ],
    },
    {
      id: 'B',
      title: 'Observations',
      subsections: [
        { title: '1. Engagement and Approach', fields: [...ENGAGEMENT_BASE] },
        { title: '2. Communication Within Task', fields: [...COMMUNICATION_BASE] },
        {
          title: '3. Social Reciprocity',
          fields: [
            radio('reciprocity', 'Reciprocity', [
              'Balanced give-and-take', 'Mostly one-sided (examiner leads)',
              'Mostly one-sided (participant monologues)', 'Minimal exchange',
            ]),
            radio('backAndForth', 'Back-and-forth flow', [
              'Smooth reciprocal flow', 'Uneven', 'One-sided', 'Minimal exchange',
            ]),
            radio('sharedAffect', 'Shared affect', ['Present', 'Reduced', 'Minimal']),
          ],
        },
        {
          title: '4. Future Orientation',
          fields: [
            radio('realismOfPlans', 'Realism of plans', [
              'Realistic and achievable', 'Somewhat realistic',
              'Vague or unrealistic', 'No plans articulated',
            ]),
            radio('socialContentOfAspirations', 'Social content of aspirations', [
              'Plans involve relationships / community', 'Some social element mentioned',
              'Exclusively solitary or object-focused', 'Not applicable',
            ]),
            radio('flexibilityOfThinking', 'Flexibility of thinking about the future', [
              'Considers multiple possibilities', 'Focused on one plan but can consider alternatives',
              'Rigidly fixed on single outcome', 'Cannot engage with the concept',
            ]),
            radio('specificityOfGoals', 'Specificity of goals', [
              'Detailed and specific', 'General but identifiable',
              'Vague or abstract', 'Cannot articulate goals',
            ]),
            radio('fixatedInterestInPlans', 'Evidence of fixated interests in plans', [
              'Plans reflect broad range of interests', 'Plans somewhat dominated by specific interest',
              'Plans entirely centred on restricted interest', 'Not applicable',
            ]),
            radio('understandingOfSteps', 'Understanding of steps needed to achieve goals', [
              'Identifies practical steps', 'General awareness of process',
              'No understanding of how to achieve goals', 'Not applicable',
            ]),
          ],
        },
        {
          title: '5. Emotional Expression',
          fields: [
            radio('affectRange', 'Range of affect when discussing the future', [
              'Appropriate and varied', 'Somewhat flat', 'Flat / incongruent',
            ]),
            radio('emotionalVocabulary', 'Emotional vocabulary', [
              'Rich / nuanced', 'Basic', 'Limited', 'Absent',
            ]),
            radio('facialExpressionCongruence', 'Facial expression congruence', [
              'Consistent with content', 'Partial / inconsistent', 'Incongruent',
            ]),
          ],
        },
        { title: '6. Restrictive/Repetitive/Sensory Behaviours', fields: [...RRBS_FIELDS] },
        {
          title: '7. Flexibility & Adaptation',
          fields: [
            radio('flexIdeas', 'Flexibility of Ideas', ['Flexible', 'Repetitive', 'Rigid']),
            radio('responseInterruption', 'Response to interruption or redirection', ['Flexible', 'Some difficulty', 'Distressed']),
            radio('perspectiveShifting', 'Ability to shift perspective (own vs others\')', [
              'Shifts readily', 'Shifts with prompting', 'Difficulty shifting',
            ]),
          ],
        },
        { title: '8. Salient Observations', fields: [...SALIENT_OBS_FIELDS] },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * CYP ASD observation tasks — the original 10 tasks for children and young people.
 * Note: config IDs are stable identifiers — the display number shown in the
 * sidebar is determined by the order the clinician adds them, not by this ID.
 */
const OBSERVATION_TASKS = [TASK_1, TASK_2, TASK_3, TASK_4, TASK_5, TASK_6, TASK_7, TASK_8, TASK_9, TASK_10];

/**
 * Adult ASD observation tasks — all 13 tasks including adult-oriented interview
 * tasks (Current Work or School, Daily Living, Plans and Hopes), ordered for
 * the adult assessment workflow.
 */
export const ADULT_ASD_OBSERVATION_TASKS = [
  TASK_2,   // 1. Telling a Story from a Book
  TASK_1,   // 2. Description of a Picture
  TASK_4,   // 3. Conversation and Reporting
  TASK_11,  // 4. Current Work or School
  TASK_5,   // 5. Social Difficulties and Annoyance
  TASK_6,   // 6. Emotions
  TASK_3,   // 7. Demonstration
  TASK_7,   // 8. Cartoons
  TASK_12,  // 9. Daily Living
  TASK_8,   // 10. Friends, Relationships and Marriage
  TASK_9,   // 11. Loneliness
  TASK_13,  // 12. Plans and Hopes
  TASK_10,  // 13. Creating a Story
];

export default OBSERVATION_TASKS;
